import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import dayjs from 'dayjs';
import { SilentError } from 'init/silent-error';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/recovery_plans';

const RecoveryPlanSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  stepsCount: z.number().int().min(0),
  debtorsCount: z.number().int().min(0),
  invoicesCount: z.number().int().min(0),
  isDefault: z.boolean(),
  updatedAt: z.iso.datetime().transform(dayjs),
});

const RecoveryPlanSummariesSchema = z.array(RecoveryPlanSummarySchema);

export type RecoveryPlanSummary = z.infer<typeof RecoveryPlanSummarySchema>;

export const RECOVERY_PLANS_QUERY_KEY = ['recoveryPlanSummaries'];

export async function loadRecoveryPlansQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL);

  const parseResult = RecoveryPlanSummariesSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'recoveryPlans');
    throw new SilentError();
  }

  return parseResult.data;
}

export function useLoadRecoveryPlans(options: QueryOptions<RecoveryPlanSummary[]> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<RecoveryPlanSummary[], ApiError>({
    queryKey: RECOVERY_PLANS_QUERY_KEY,
    queryFn: () => loadRecoveryPlansQueryFn(axiosInstance),
    ...options,
  });

  return addResourceNameToQueryResult<RecoveryPlanSummary[], ApiError, 'recoveryPlans'>(
    'recoveryPlans',
    queryResult,
  );
}
